/**
 * GH#1746 — el alta de propiedad mandaba al POST /properties los valores de
 * los selects EN ESPAÑOL (type «apartamento», status «disponible»), `features`
 * como objeto y sin `country`: la API respondía 400 con 16 mensajes y un
 * tenant nuevo no podía cargar su primera propiedad.
 *
 * Acá vive la traducción del formulario al contrato de PropertyCreateDTO
 * (apps/api/src/modules/catalog/dto/property.dto.ts). Es una función pura para
 * poder probarla sin montar el wizard.
 */

import { CIUDADES, SECTORES, etiquetaDe } from '../constants/ciudades-sectores';

/** Tipos que acepta la API (@IsIn de PropertyCreateDTO.propertyType). */
export const TIPOS_API = [
  'apartment', 'penthouse', 'studio', 'house', 'duplex', 'townhouse', 'villa',
  'finca', 'land', 'commercial', 'office', 'warehouse', 'industrial',
  'building', 'resort', 'hotel', 'land_development', 'marina',
] as const;

/** Estados de PropertyStatus en la API. */
export const ESTADOS_API = [
  'available', 'sold', 'rented', 'negotiation', 'temp_rental', 'reserved', 'off_market', 'draft',
] as const;

/** Condiciones de PropertyCondition en la API. */
export const CONDICIONES_API = [
  'new', 'in_plans', 'under_construction', 'used', 'renovated', 'immediate_delivery',
  'furnished', 'semi_furnished', 'unfurnished', 'turn_key',
] as const;

/**
 * Operaciones que acepta la API (@IsIn de PropertyCreateDTO.operation). El formulario
 * ofrece dos: Venta y Alquiler (largo plazo).
 *
 * GH#1850 — el alta no pedía la operación y la propiedad nacía sin ella. Es lo primero
 * que pregunta un cliente de un CRM inmobiliario.
 */
export const OPERACIONES_API = [
  'sale', 'long_term_rental', 'vacation_rental', 'presale', 'in_plans',
  'rent_to_own', 'auction', 'timeshare', 'exchange',
] as const;

/** Lo que ofrece el formulario, con el value que viaja a la API. */
export const OPERACIONES_FORM = [
  { value: 'sale', label: 'Venta' },
  { value: 'long_term_rental', label: 'Alquiler' },
] as const;

const OPERACION_ES: Record<string, string> = {
  venta: 'sale',
  alquiler: 'long_term_rental',
};

const TIPO_ES: Record<string, string> = {
  apartamento: 'apartment',
  penthouse: 'penthouse',
  estudio: 'studio',
  casa: 'house',
  'dúplex': 'duplex',
  duplex: 'duplex',
  villa: 'villa',
  finca: 'finca',
  solar: 'land',
  local: 'commercial',
  oficina: 'office',
};

const ESTADO_ES: Record<string, string> = {
  disponible: 'available',
  reservado: 'reserved',
  vendido: 'sold',
  alquilado: 'rented',
  // «En mantenimiento» = fuera de mercado mientras tanto.
  mantenimiento: 'off_market',
};

// El select de condición describe el ESTADO FÍSICO (excelente, buena…) y la
// API describe la ETAPA (nueva, usada…). Sólo «nueva construcción» tiene
// equivalente exacto; el resto es una propiedad usada.
const CONDICION_ES: Record<string, string> = {
  nueva_construccion: 'new',
  excelente: 'used',
  buena: 'used',
  regular: 'used',
  requiere_trabajo: 'used',
};

/** Traduce un valor del select; si ya viene en el formato de la API, lo deja. */
function traducir(valor: unknown, mapa: Record<string, string>, validos: readonly string[]): string | undefined {
  if (typeof valor !== 'string' || !valor.trim()) return undefined;
  const v = valor.trim().toLowerCase();
  if ((validos as readonly string[]).includes(v)) return v;
  return mapa[v];
}

const numero = (v: unknown): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
};

/** País por defecto: el que ya usan las propiedades cargadas en prod. */
export const PAIS_POR_DEFECTO = 'Republica Dominicana';

export interface FormularioPropiedad {
  title: string;
  description?: string;
  propertyType?: string;
  /** venta | alquiler del formulario (o ya en formato API). */
  operation?: string;
  status?: string;
  condition?: string;
  // `amount` opcional: el formulario abre VACÍO (GH#1850); la validación lo exige antes
  // de llegar acá, y numero() descarta undefined.
  price: { amount?: number | string; currency?: string };
  area?: number | string;
  bedrooms?: number | string;
  bathrooms?: number | string;
  parkingSpaces?: number | string;
  floors?: number | string;
  constructionYear?: number | string;
  address: string;
  city: string;
  sector?: string;
  coordinates?: { lat?: number; lng?: number };
  amenities?: string[];
  isFeatured?: boolean;
}

/**
 * Cuerpo del POST /properties. Sólo campos de PropertyCreateDTO, planos, sin
 * `features` (en la API es una lista de textos, no el objeto del formulario)
 * y sin imágenes: los archivos se suben aparte a POST /properties/:id/images.
 */
export function cuerpoCrearPropiedad(f: FormularioPropiedad): Record<string, unknown> {
  const cuerpo: Record<string, unknown> = {
    title: f.title?.trim(),
    description: f.description?.trim() || undefined,
    propertyType: traducir(f.propertyType, TIPO_ES, TIPOS_API),
    operation: traducir(f.operation, OPERACION_ES, OPERACIONES_API),
    status: traducir(f.status, ESTADO_ES, ESTADOS_API),
    condition: traducir(f.condition, CONDICION_ES, CONDICIONES_API),
    price: numero(f.price?.amount),
    currency: f.price?.currency || undefined,
    size: numero(f.area),
    bedrooms: numero(f.bedrooms),
    bathrooms: numero(f.bathrooms),
    parkingSpaces: numero(f.parkingSpaces),
    floors: numero(f.floors),
    yearBuilt: numero(f.constructionYear),
    address: f.address?.trim(),
    // GH#1850 (menor 1): el select guarda el value («santo_domingo») y esto lo mandaba
    // crudo, así que TODA propiedad quedaba con city «santo_domingo» y los listados lo
    // mostraban así. Se manda el nombre legible («Santo Domingo»). Si el value no está en
    // la lista (dato viejo / escrito a mano) pasa tal cual.
    city: etiquetaDe(CIUDADES, f.city),
    // El formulario pide ciudad y sector. El sector es el barrio; la API exige
    // además `state` (provincia), que el formulario no pide: va la ciudad.
    neighborhood: etiquetaDe(SECTORES, f.sector),
    state: etiquetaDe(CIUDADES, f.city),
    country: PAIS_POR_DEFECTO,
    latitude: numero(f.coordinates?.lat),
    longitude: numero(f.coordinates?.lng),
    amenities: Array.isArray(f.amenities) && f.amenities.length ? f.amenities : undefined,
    isFeatured: f.isFeatured || undefined,
  };
  for (const k of Object.keys(cuerpo)) if (cuerpo[k] === undefined) delete cuerpo[k];
  return cuerpo;
}

/**
 * GH#1746 — la API (class-validator de NestJS) devuelve los errores de
 * validación como `message: string[]`. El wizard lo metía tal cual en un
 * string, y React los renderiza PEGADOS sin separador
 * («Tipo no válidoLa dirección debe…»). Esto los une legibles.
 *
 * @param raw  `error.response?.data?.message` (string | string[] | otro)
 * @param fallback texto cuando no hay mensaje utilizable
 */
export function mensajeError(raw: unknown, fallback: string): string {
  if (Array.isArray(raw)) {
    const items = raw.filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
    if (items.length) return items.join(' · ');
    return fallback;
  }
  if (typeof raw === 'string' && raw.trim().length > 0) return raw;
  return fallback;
}
